import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, money, fmtDate, fmtHours, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Receipt, CheckCircle2, Ban, FileDown, Mail, Paperclip, XCircle, MessageCircle, Share2, ExternalLink, Copy, QrCode, Layers, FileBadge2, Search, FileText, Barcode, Clock, X, Filter, Hourglass } from "lucide-react";
import PixChaveModal from "../components/PixChaveModal";
import IntegratedPixModal from "../components/IntegratedPixModal";
import AglutinarDialog from "../components/AglutinarDialog";
import SummaryCard from "../components/SummaryCard";
import { shareQrToWhatsapp } from "../lib/pixShare";

export default function Faturamento() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  // Pacote de Correções, item 1 — causa raiz do bug: o estado iniciava
  // como "" (nenhuma seleção "visível" no <Select>, mas buscarElegiveis()
  // já tratava "" igual a "__all__" na busca). gerarFaturamento(), porém,
  // só reconhecia o sentinel literal "__all__" como modo "Todos os
  // clientes" — com cliente="", ela enviava cliente_id="" ao backend, que
  // rejeitava com "Cliente inválido". Iniciar diretamente em "__all__"
  // elimina esse estado ambíguo (o <Select> já nasce mostrando "Todos os
  // clientes", condizente com o que a busca já fazia).
  const [cliente, setCliente] = useState("__all__");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [boletos, setBoletos] = useState([]);
  const [boletoOpen, setBoletoOpen] = useState(null); // invoice
  const [venc, setVenc] = useState(new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10));
  // Fundação Multi-Provider de Pagamentos — contas PADRÃO do tenant (uma
  // por provider, no máximo — is_default é único por provider), usadas
  // para decidir se "Gerar boleto" oferece emissão REAL (Mercado Pago e/ou
  // Asaas) além do modo simulado já existente. Nunca escolhe uma conta
  // arbitrariamente: só entra aqui uma conta explicitamente marcada como
  // padrão pelo Admin em Configurações → Bancos e Pagamentos.
  const [defaultPaymentAccounts, setDefaultPaymentAccounts] = useState([]);
  const [boletoProviderId, setBoletoProviderId] = useState("");
  // Item 3 — "Gerar PIX" unificado: quando aberto a partir do atalho de
  // PIX (nunca do "Gerar boleto"), o diálogo já existente abaixo mostra
  // só o botão de PIX, nunca o de boleto — mesmo diálogo/lógica de conta
  // padrão, sem duplicar nada.
  const [pixOnlyMode, setPixOnlyMode] = useState(false);
  const [generatingInstrument, setGeneratingInstrument] = useState(false);
  const [syncingBoleto, setSyncingBoleto] = useState(null);
  // PIX por chave — modalidade INDEPENDENTE do PIX integrado (Mercado
  // Pago) acima: QR estático gerado 100% pela LOOTUS, sem integração
  // bancária. Só oferecida quando o tenant tem ao menos uma chave ATIVA
  // configurada (Configurações → Bancos e Pagamentos → PIX por chave).
  const [pixKeyAccounts, setPixKeyAccounts] = useState([]);
  const [pixChaveOpen, setPixChaveOpen] = useState(null); // invoice
  const [pixChaveVenc, setPixChaveVenc] = useState(new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10));
  const [pixChaveAccountId, setPixChaveAccountId] = useState("");
  const [generatingPixChave, setGeneratingPixChave] = useState(false);
  const [pixChaveModalData, setPixChaveModalData] = useState(null); // boleto pix_chave
  // Item 3 — escolha do tipo de PIX (Asaas/MP integrado x chave cadastrada)
  // só aparece quando as DUAS opções existem para o tenant; guarda a
  // fatura (invoice) para a qual a pergunta foi aberta.
  const [pixChoiceOpen, setPixChoiceOpen] = useState(null); // invoice
  // Correção PIX Asaas — "Ver PIX" para PIX via provedor INTEGRADO
  // (Mercado Pago/Asaas), independente do PIX por chave acima.
  const [integratedPixModalData, setIntegratedPixModalData] = useState(null); // boleto mercado_pago/asaas, instrumento_tipo="pix"
  const [confirmRecebimentoOpen, setConfirmRecebimentoOpen] = useState(null); // boleto
  const [confirmRecebimentoForm, setConfirmRecebimentoForm] = useState({ valor_recebido: "", data_recebimento: "", observacao: "" });
  const [confirmingRecebimento, setConfirmingRecebimento] = useState(false);
  // Isolamento Estrito do SMTP do Tenant — true/false quando conhecido,
  // null enquanto não sabemos (perfil sem acesso à configuração). Usado só
  // para intercepção de UX; o backend sempre valida de novo (item 18).
  const [tenantEmailConfigured, setTenantEmailConfigured] = useState(null);
  const EMAIL_NOT_CONFIGURED_MSG = "E-mail de cobrança não configurado. Acesse Configurações → E-mail de cobrança para configurar.";
  // NFS-e Nacional, Fase 1 (preparação/Produção Restrita) — só mostra o
  // atalho "NFS-e" por fatura quando a empresa habilitou o módulo em
  // Configurações → Fiscal / NFS-e (ver docs/NFSE_NACIONAL.md).
  const [nfseAtiva, setNfseAtiva] = useState(false);
  const [nfseByInvoice, setNfseByInvoice] = useState({}); // invoice_id -> nfse_document
  // Cards de acompanhamento (delta 2026-09-16, referência visual fornecida)
  // — filtro client-side sobre `boletos` (já carregado inteiro por
  // load(), sem paginação server-side — mesmo raciocínio já usado nos
  // cards de Fichas: nunca filtra só uma página). null = nenhum filtro
  // (mostra todos os boletos, comportamento atual preservado).
  const [boletoCardFilter, setBoletoCardFilter] = useState(null); // null | "vencido" | "pago" | "em_aberto"
  // Ajuste de homologação — Ciclo correto da cobrança, item 2: filtro de
  // situação (Ativos/Cancelados/Todos), compartilhado por Faturamentos e
  // Cobranças — "Ativos" é o padrão (cancelados saem da visão operacional
  // padrão, sem apagar nada; "Todos"/"Cancelados" continuam acessíveis).
  const [statusFiltro, setStatusFiltro] = useState("ativos"); // "ativos" | "cancelados" | "todos"
  // Item 12 do pedido — filtros recolhíveis (mesmo padrão de Fichas.jsx):
  // painel começa FECHADO, nunca exige preencher nada para ver
  // Faturamentos/Cobranças (que já carregam sozinhos via load() abaixo).
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtrosAtivos = cliente !== "__all__" || !!start || !!end || statusFiltro !== "ativos";

  const load = async () => {
    try {
      const [c, inv, b, pacc, pka, es, fiscal, nf] = await Promise.all([
        api.get("/clients"),
        api.get("/invoices"),
        api.get("/boletos"),
        api.get("/config/payment-accounts").catch(() => ({ data: [] })),
        api.get("/config/pix-key-accounts").catch(() => ({ data: [] })),
        // Isolamento Estrito do SMTP do Tenant, item 8 — só para decidir se
        // intercepta "Enviar por e-mail" no frontend ANTES de chamar o
        // backend; o backend continua sendo a regra final de qualquer
        // forma (item 18). 403 (perfil sem acesso a /config/email-settings,
        // ex.: supervisor) é tratado como "desconhecido", nunca quebra a tela.
        api.get("/config/email-settings").catch(() => ({ data: null })),
        api.get("/config/fiscal/nfse").catch(() => ({ data: null })),
        api.get("/nfse").catch(() => ({ data: [] })),
      ]);
      setClients(c.data); setInvoices(inv.data); setBoletos(b.data);
      setDefaultPaymentAccounts(pacc.data.filter(a => a.is_default && a.active));
      setPixKeyAccounts(pka.data);
      setTenantEmailConfigured(es.data ? !!es.data.password_configured : null);
      setNfseAtiva(!!fiscal.data?.active);
      const byInv = {};
      (nf.data || []).forEach(n => { if (n.status !== "cancelada") byInv[n.invoice_id] = n; });
      setNfseByInvoice(byInv);
    } catch { /* Correções de homologação (P1) — nunca deixar a promise sem tratamento */ }
  };

  useEffect(() => { load(); }, []);

  // Pacote pós-homologação, item 3: "Todos os clientes" (sentinel "__all__")
  // não exige mais selecionar um cliente específico para consultar fichas
  // elegíveis. O backend (/invoices/eligible) já restringe tudo ao tenant
  // da sessão de qualquer forma — cliente_id vazio só amplia a busca para
  // "todos os clientes DESTE tenant", nunca de outra empresa.
  const buscarElegiveis = async () => {
    const cliente_id = cliente === "__all__" ? "" : cliente;
    const { data } = await api.get("/invoices/eligible", { params: { cliente_id, start, end } });
    setTickets(data);
    const sel = {}; data.forEach(t => sel[t.id] = true);
    setSelected(sel);
  };

  // Item 11/12 do pedido — busca sozinha, sem filtro nenhum, assim que a
  // tela abre (mesmo critério/endpoint da listagem real, /invoices/eligible)
  // para o card "Aguardando faturamento" já nascer com o número certo e
  // para nunca exigir clicar em "Filtrar" antes de ver alguma coisa.
  useEffect(() => { buscarElegiveis(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // "Limpar filtros" (delta 2026-09-17) — volta ao estado padrão coerente:
  // cliente/período do painel de busca de fichas elegíveis E o card de
  // status ativo da tabela de Boletos abaixo (dois filtros independentes
  // nesta mesma tela). Nunca refaz a busca sozinho — mesmo espírito de
  // "Buscar fichas" ser uma ação explícita.
  const limparFiltros = () => {
    setCliente("__all__"); setStart(""); setEnd("");
    setTickets([]); setSelected({});
    setBoletoCardFilter(null);
    setStatusFiltro("ativos");
  };

  const selectedTickets = tickets.filter(t => selected[t.id]);
  const totalHoras = selectedTickets.reduce((a, t) => a + (t.horas_faturaveis || 0), 0);
  const totalValor = selectedTickets.reduce((a, t) => a + (t.valor_total || 0), 0);

  // Ajustes finais da Homologação V1, item 1 — "Selecionar todos" atua
  // exclusivamente sobre `tickets`, que já é o conjunto de fichas elegíveis
  // (backend /invoices/eligible) filtrado pela empresa/período escolhidos —
  // nunca existe um registro "oculto fora do conjunto apresentado" para
  // marcar: o que está na tela é exatamente o que pode ser selecionado.
  const allSelected = tickets.length > 0 && tickets.every(t => selected[t.id]);
  const someSelected = tickets.some(t => selected[t.id]);
  const headerCheckboxRef = useRef(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);
  const toggleSelectAll = () => {
    const next = {};
    if (!allSelected) tickets.forEach(t => { next[t.id] = true; });
    setSelected(next);
  };

  // Aglutinação (2+ fichas) e escolha de forma de pagamento (1 ficha, Etapa
  // 1 da evolução de cobrança) — mesmo diálogo, mesmo endpoint POST
  // /invoices, mesma lógica de parcelamento já existente; só habilita com
  // fichas do MESMO cliente selecionadas (>= 1, não mais >= 2).
  const [aglutinarOpen, setAglutinarOpen] = useState(false);
  const aglutClienteIds = [...new Set(selectedTickets.map(t => t.cliente_id))];
  const podeAglutinar = selectedTickets.length >= 1 && aglutClienteIds.length === 1;
  const aglutClienteId = aglutClienteIds[0];

  // Achado da homologação (item 7) — o resumo agregado ("Falhas: N") nunca
  // mostrava a RAZÃO de cada falha (data.erros, já devolvido pelo backend
  // desde a Etapa 4C: `{erros: [...]}`, truncado a 15). Isso mascarava a
  // causa real na UI. Agora sempre exibida junto ao resumo.
  const resumoEmissao = (data) => {
    const base = `Emitidas: ${data.emitidas} · Já emitidas: ${data.ja_emitidas}` + (data.falhas ? ` · Falhas: ${data.falhas}` : "");
    if (data.falhas && data.erros?.length) return `${base} — ${data.erros.join("; ")}`;
    return base;
  };

  // Ajuste de homologação — Ciclo correto da cobrança, item 3: validação
  // PRÉVIA do cadastro do cliente (backend é a única autoridade — GET
  // /clients/{id}/cobranca-requisitos reaproveita os MESMOS validadores
  // já usados na emissão real, nunca uma lista de regras duplicada aqui).
  // Bloqueia a ação com uma mensagem clara + atalho "Editar cliente" (em
  // nova aba — nunca perde a seleção de fichas/diálogo aberto nesta
  // página) em vez de deixar a emissão seguir e falhar (achado real da
  // homologação: 7 falhas só descobertas DEPOIS do clique). Falha ao
  // CONSULTAR a validação (rede/backend fora do ar) nunca trava o fluxo —
  // a emissão real continua sendo a defesa final de qualquer forma.
  // Últimos ajustes antes da produção, item 2 — `pixModalidade`/`incluiBoleto`
  // deixam a validação ciente da modalidade escolhida (PIX integrado x
  // PIX por chave têm requisitos totalmente diferentes — nunca exige
  // CPF/CNPJ do cliente para uma cobrança 100% PIX por chave).
  const validarCadastroCliente = async (clienteId, { pixModalidade, incluiBoleto } = {}) => {
    try {
      const params = {};
      if (pixModalidade) params.pix_modalidade = pixModalidade;
      if (incluiBoleto) params.inclui_boleto = true;
      const { data } = await api.get(`/clients/${clienteId}/cobranca-requisitos`, { params });
      if (!data.ok) {
        toast.error(data.mensagem || "Cadastro do cliente incompleto para emitir cobrança.", {
          duration: 20000,
          action: { label: "Editar cliente", onClick: () => window.open(`/clientes?edit=${clienteId}`, "_blank", "noopener,noreferrer") },
        });
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  // Últimos ajustes antes da produção, item 2 / Delta Parcelamento via
  // PIX — entrada E as parcelas do saldo podem ter modalidades PIX
  // independentes uma da outra (ex.: entrada em boleto + parcelas em PIX
  // por chave) — a validação prévia precisa considerar SÓ o que está
  // realmente pendente nesta fatura, qualquer que seja a origem (entrada
  // ou parcela do saldo): se tudo pendente for PIX por chave, nunca
  // exige cadastro completo do cliente; se algo pendente precisar de
  // boleto/PIX integrado, o requisito entra na validação também.
  const parcelasPendentesDaFatura = (invoiceId) =>
    boletos.filter(b => b.invoice_id === invoiceId && !b.external_charge_id && !b.pix_txid && b.status === "em_aberto");

  const emitirParcelas = async (i) => {
    const pendentes = parcelasPendentesDaFatura(i.id);
    const primeiraPixPendente = pendentes.find(p => p.instrumento_tipo === "pix");
    const precisaBoleto = pendentes.some(p => !(p.instrumento_tipo === "pix" && p.pix_modalidade === "chave"));
    const ok = await validarCadastroCliente(i.cliente_id, {
      pixModalidade: primeiraPixPendente?.pix_modalidade,
      incluiBoleto: precisaBoleto,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/invoices/${i.id}/parcelas/emitir`, { instrumento_tipo: "boleto" });
      const resumo = resumoEmissao(data);
      if (data.falhas) toast.warning(resumo, { duration: 15000 });
      else toast.success(resumo || "Parcelas emitidas");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Ajuste final de homologação (item 1 do pedido) — a linha do
  // faturamento misto oferecia 2 ações separadas ("Emitir boleto (N)" +
  // "Emitir PIX"), confuso para o usuário. Uma ÚNICA ação "Emitir
  // cobrança" dispara os DOIS componentes (o fluxo da Etapa 4C continua
  // exigindo component_id explícito por chamada — nunca um lote
  // invoice-wide ambíguo no backend; a UI só orquestra as 2 chamadas e
  // consolida o resultado). `emittingComponent` guarda "invoiceId:cobranca"
  // e desabilita o botão único enquanto QUALQUER uma das 2 chamadas está
  // em andamento — evita clique duplo; a proteção REAL contra concorrência
  // continua sendo o claim atômico do backend (Etapa 4C).
  const [emittingComponent, setEmittingComponent] = useState(null);
  const emitirCobrancaMista = async (i) => {
    const key = `${i.id}:cobranca`;
    setEmittingComponent(key);
    // Últimos ajustes antes da produção, item 2 — misto sempre tem boleto
    // (comp2, sempre precisa de provider); o PIX (comp1) pode ser
    // integrado ou por chave — a validação precisa saber qual.
    const mistoPixDoc = boletos.find(b => b.invoice_id === i.id && b.component_id === "comp1");
    const ok = await validarCadastroCliente(i.cliente_id, { pixModalidade: mistoPixDoc?.pix_modalidade, incluiBoleto: true });
    if (!ok) { setEmittingComponent(null); return; }
    try {
      const chamada = async (componentId, label) => {
        try {
          const { data } = await api.post(`/invoices/${i.id}/parcelas/emitir`, { component_id: componentId });
          return data;
        } catch (e) {
          // uma falha TOTAL de uma chamada (ex.: 503 de readiness) nunca
          // engole o resultado da outra — vira mais 1 item de erro no
          // resumo consolidado, com o rótulo do componente.
          return { emitidas: 0, ja_emitidas: 0, falhas: 1, erros: [`${label}: ${formatApiError(e)}`] };
        }
      };
      const [pix, boleto] = await Promise.all([chamada("comp1", "PIX"), chamada("comp2", "Boleto")]);
      const consolidado = {
        emitidas: pix.emitidas + boleto.emitidas,
        ja_emitidas: pix.ja_emitidas + boleto.ja_emitidas,
        falhas: pix.falhas + boleto.falhas,
        erros: [...(pix.erros || []), ...(boleto.erros || [])],
      };
      const resumo = resumoEmissao(consolidado);
      if (consolidado.falhas) toast.warning(resumo, { duration: 15000 });
      else toast.success(resumo || "Cobrança emitida");
      load();
    } finally {
      setEmittingComponent(null);
    }
  };

  const gerarFaturamento = async () => {
    const ticket_ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!ticket_ids.length) return toast.error("Selecione ao menos uma ficha");
    // Um faturamento continua sendo de UM único cliente (regra já
    // homologada, preservada integralmente) — em modo "Todos os clientes"
    // a grade pode mostrar fichas de vários, então confirma aqui que a
    // seleção marcada pertence a um único cliente antes de enviar.
    const clientesSelecionados = new Set(selectedTickets.map(t => t.cliente_id));
    if (clientesSelecionados.size > 1) {
      return toast.error("Selecione fichas pertencentes ao mesmo cliente para gerar o faturamento.");
    }
    const cliente_id = cliente === "__all__" ? [...clientesSelecionados][0] : cliente;
    try {
      // Delta "Faturamento direto" — mesma chamada de sempre
      // (forma_pagamento="avista" no backend, nunca renomeado — só a
      // apresentação ao usuário muda): cria SÓ a fatura, sem boleto/PIX,
      // sem instrumento fixado. Nunca confundir com "à vista" dentro do
      // diálogo de forma de pagamento (escolha deliberada entre 3
      // opções) — aqui o usuário nem chega a escolher forma nenhuma.
      await api.post("/invoices", { cliente_id, ticket_ids });
      toast.success("Faturamento direto gerado — escolha a cobrança quando quiser");
      setTickets([]); setSelected({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Fundação Multi-Provider de Pagamentos — abre "Gerar boleto" já
  // selecionando a única conta padrão disponível (ou a primeira, quando o
  // tenant tem conta padrão ativa em mais de um provider — o Admin escolhe
  // explicitamente no diálogo antes de emitir).
  const openBoletoDialog = (i, { pixOnly = false } = {}) => {
    setBoletoOpen(i);
    setBoletoProviderId(defaultPaymentAccounts[0]?.id || "");
    setPixOnlyMode(pixOnly);
  };
  const activeBoletoAccount = defaultPaymentAccounts.find(a => a.id === boletoProviderId) || null;

  // Fundação Multi-Provider de Pagamentos — emissão REAL via a conta
  // PADRÃO escolhida no diálogo (Mercado Pago ou Asaas). Nunca envia valor
  // (o backend resolve tudo a partir do faturamento); só a data de
  // vencimento escolhida no diálogo.
  const gerarInstrumentoReal = async (tipo) => {
    if (!boletoOpen || !activeBoletoAccount) return;
    const sufixoProvider = activeBoletoAccount.provider === "asaas" ? "asaas" : "mercadopago";
    const nomeProvider = activeBoletoAccount.provider === "asaas" ? "Asaas" : "Mercado Pago";
    setGeneratingInstrument(true);
    if (!(await validarCadastroCliente(boletoOpen.cliente_id))) { setGeneratingInstrument(false); return; }
    try {
      await api.post(`/invoices/${boletoOpen.id}/${tipo === "pix" ? "pix" : "boleto"}-${sufixoProvider}`, { vencimento: venc });
      toast.success(tipo === "pix" ? `PIX gerado no ${nomeProvider}` : `Boleto gerado no ${nomeProvider}`);
      setBoletoOpen(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGeneratingInstrument(false); }
  };

  // PIX por chave — geração 100% local (sem Mercado Pago/API bancária).
  // "vencimento" aqui é só uma data de REFERÊNCIA interna — nunca uma
  // expiração real do QR (o backend chama o mesmo campo por compatibilidade
  // com o modelo de boleto, mas o rótulo na UI é sempre "Data de referência").
  const activePixKeyAccounts = pixKeyAccounts.filter(a => a.active);
  const openPixChaveDialog = (i) => {
    setPixChaveOpen(i);
    setPixChaveVenc(new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10));
    setPixChaveAccountId("");
  };
  const gerarPixChave = async () => {
    if (!pixChaveOpen) return;
    setGeneratingPixChave(true);
    try {
      const body = { vencimento: pixChaveVenc };
      if (pixChaveAccountId) body.pix_key_account_id = pixChaveAccountId;
      const { data } = await api.post(`/invoices/${pixChaveOpen.id}/pix-chave`, body);
      toast.success("PIX por chave gerado");
      setPixChaveOpen(null);
      setPixChaveModalData(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGeneratingPixChave(false); }
  };

  // Item 3 — entrada ÚNICA de "Gerar PIX": decide entre os dois fluxos JÁ
  // EXISTENTES (integrado via Asaas/Mercado Pago, ou por chave cadastrada)
  // sem duplicar nenhuma lógica de geração. As duas contas são as MESMAS
  // já carregadas em load() — o backend sempre revalida de novo (item 14),
  // nunca confia nesta decisão do frontend.
  const abrirGerarPix = (i) => {
    const temIntegrado = defaultPaymentAccounts.length > 0;
    const temChave = activePixKeyAccounts.length > 0;
    if (temIntegrado && temChave) {
      setPixChoiceOpen(i);
    } else if (temIntegrado) {
      openBoletoDialog(i, { pixOnly: true });
    } else if (temChave) {
      openPixChaveDialog(i);
    } else {
      toast.error("Nenhuma forma de recebimento PIX configurada. Configure em Configurações → Bancos e pagamentos.");
    }
  };
  // Rótulo do provedor integrado para a pergunta de escolha — dinâmico
  // (não hardcoda "Asaas" quando o tenant tem Mercado Pago configurado).
  const integradoProviderLabel = defaultPaymentAccounts.length === 1
    ? (defaultPaymentAccounts[0].provider === "asaas" ? "Asaas" : "Mercado Pago")
    : "conta integrada";

  const openConfirmRecebimento = (b) => {
    setConfirmRecebimentoOpen(b);
    setConfirmRecebimentoForm({ valor_recebido: b.valor != null ? String(b.valor) : "", data_recebimento: new Date().toISOString().slice(0, 10), observacao: "" });
  };
  const doConfirmRecebimento = async () => {
    if (!confirmRecebimentoOpen) return;
    setConfirmingRecebimento(true);
    try {
      const body = {};
      if (confirmRecebimentoForm.valor_recebido !== "") body.valor_recebido = Number(confirmRecebimentoForm.valor_recebido);
      if (confirmRecebimentoForm.data_recebimento) body.data_recebimento = confirmRecebimentoForm.data_recebimento;
      if (confirmRecebimentoForm.observacao.trim()) body.observacao = confirmRecebimentoForm.observacao.trim();
      await api.post(`/boletos/${confirmRecebimentoOpen.id}/pay`, body);
      toast.success("Recebimento confirmado");
      setConfirmRecebimentoOpen(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirmingRecebimento(false); }
  };

  const sincronizarBoleto = async (b) => {
    setSyncingBoleto(b.id);
    const sufixoProvider = b.modo === "asaas" ? "asaas" : "mercadopago";
    const nomeProvider = b.modo === "asaas" ? "Asaas" : "Mercado Pago";
    try { await api.post(`/boletos/${b.id}/sync-${sufixoProvider}`); toast.success(`Situação sincronizada com o ${nomeProvider}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncingBoleto(null); }
  };

  // "Sincronizar todos" — consulta de uma vez o status de todos os boletos
  // integrados (Mercado Pago/Asaas) DESTA empresa. O backend usa o mesmo
  // núcleo do botão individual, isola por company_id da sessão e nunca
  // deixa a falha de um boleto interromper os demais.
  const [syncingAll, setSyncingAll] = useState(false);
  const boletosSincronizaveis = boletos.filter(b => b.modo === "mercado_pago" || b.modo === "asaas").length;
  const sincronizarTodos = async () => {
    if (syncingAll) return;
    setSyncingAll(true);
    try {
      const { data: r } = await api.post("/boletos/sync-all");
      const resumo = `Sincronizados: ${r.sincronizados} · Atualizados: ${r.atualizados} · Sem alteração: ${r.sem_alteracao}`
        + (r.ignorados ? ` · Ignorados: ${r.ignorados}` : "");
      if (r.falhas > 0) toast.warning(`${resumo} · Falhas: ${r.falhas}`);
      else toast.success(resumo);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncingAll(false); }
  };

  const pay = async (b) => {
    try { await api.post(`/boletos/${b.id}/pay`); toast.success("Boleto marcado como pago"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  // Últimos ajustes antes da produção, item 1 — cancelamento de boleto/PIX
  // individual passa a exigir justificativa, mesmo padrão do cancelamento
  // de faturamento (cancelInvoiceOpen/cancelInvoiceReason) já homologado.
  const [cancelBoletoOpen, setCancelBoletoOpen] = useState(null); // boleto
  const [cancelBoletoReason, setCancelBoletoReason] = useState("");
  const doCancelBoleto = async () => {
    if (!cancelBoletoReason.trim()) return toast.error("Informe o motivo do cancelamento");
    const b = cancelBoletoOpen;
    try {
      const { data } = await api.post(`/boletos/${b.id}/cancel`, { reason: cancelBoletoReason });
      if ((b.modo === "mercado_pago" || b.modo === "asaas") && data.cancelado_no_provider === false) {
        const nomeProvider = b.modo === "asaas" ? "Asaas" : "Mercado Pago";
        toast.warning(data.cancelado_detail || `Cancelado apenas no LOOTUS — não foi possível confirmar o cancelamento no ${nomeProvider}.`);
      } else {
        toast.success("Boleto cancelado");
      }
      setCancelBoletoOpen(null); setCancelBoletoReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Ajustes finais da Homologação V1, item 2 — cancelamento/estorno de
  // faturamento. Motivo obrigatório (mesmo padrão de Rejeitar Ficha, em
  // Aprovacoes.jsx); o backend valida de novo o estado (faturamento pago
  // ou já cancelado é rejeitado com mensagem explicando o motivo).
  const [cancelInvoiceOpen, setCancelInvoiceOpen] = useState(null);
  const [cancelInvoiceReason, setCancelInvoiceReason] = useState("");
  const doCancelInvoice = async () => {
    if (!cancelInvoiceReason.trim()) return toast.error("Informe o motivo do cancelamento");
    try {
      await api.post(`/invoices/${cancelInvoiceOpen.id}/cancel`, { reason: cancelInvoiceReason });
      toast.success("Faturamento cancelado");
      setCancelInvoiceOpen(null); setCancelInvoiceReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Ajustes finais da Homologação V1, item 3 — anulação de baixa manual.
  // Só oferecida na interface quando a baixa NÃO foi por conciliação
  // (b.baixa_via_reconciliacao) — e o backend valida de novo, então uma
  // chamada direta pela API também é rejeitada.
  const [reverseBoletoOpen, setReverseBoletoOpen] = useState(null);
  const [reverseBoletoReason, setReverseBoletoReason] = useState("");
  const doReverseBoleto = async () => {
    if (!reverseBoletoReason.trim()) return toast.error("Informe o motivo da anulação");
    try {
      await api.post(`/boletos/${reverseBoletoOpen.id}/reverse`, { reason: reverseBoletoReason });
      toast.success("Baixa anulada");
      setReverseBoletoOpen(null); setReverseBoletoReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dlInvoice = (i) => {
    const url = `${API_BASE}/invoices/${i.id}/pdf`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = `faturamento_${i.numero}.pdf`; a.click();
      }).catch(() => toast.error("Falha ao gerar PDF"));
  };
  const resendInvoice = async (i) => {
    if (tenantEmailConfigured === false) { toast.error(EMAIL_NOT_CONFIGURED_MSG); return; }
    try { const { data } = await api.post(`/invoices/${i.id}/resend`); if (data.sent) toast.success("E-mail reenviado ao cliente"); else toast.info("Cliente sem e-mail cadastrado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Fechamento da homologação, itens 7-13 — "Compartilhar" do boleto
  // operacional, mesma dinâmica já usada no Admin Master/Gestão Comercial:
  // um único botão com E-mail/WhatsApp, nunca um ícone isolado com fluxo
  // próprio. WhatsApp reaproveita o mesmo helper gratuito (wa.me) desde a
  // rodada anterior; e-mail reaproveita a infraestrutura já homologada de
  // reenvio de faturamento (_notify_invoice) — nunca aceita e-mail vindo do
  // frontend, sempre o e-mail cadastrado do cliente do tenant.
  // Item 3 do pedido — link do WhatsApp SEM abrir a janela ainda: os
  // modais de PIX (PixChaveModal/IntegratedPixModal) precisam do texto
  // pronto para decidir, ELES, entre Web Share API (com o QR já
  // renderizado ali) ou o fallback de sempre — nunca uma segunda chamada
  // ao backend nem um link reconstruído no frontend.
  const getBoletoWhatsappLink = async (b) => {
    const { data } = await api.post(`/boletos/${b.id}/whatsapp-share-link`);
    return data.wa_link;
  };

  // Correção — Modal Faturamento/Compartilhamento, item 2 — nome de
  // arquivo do QR compartilhado/baixado sempre identificando a cobrança
  // exata (ex. "qr-pix-F00002-1-5.png"), nunca um nome genérico.
  const qrFilenameFor = (b) => `qr-pix-${(b?.numero_documento || "cobranca").replace(/\//g, "-")}.png`;

  const shareBoleto = async (b, channel) => {
    // Isolamento Estrito do SMTP do Tenant, item 8/9 — só intercepta o
    // canal E-MAIL; WhatsApp, cópia, download e link público continuam
    // disponíveis normalmente mesmo sem SMTP configurado.
    if (channel !== "whatsapp" && tenantEmailConfigured === false) { toast.error(EMAIL_NOT_CONFIGURED_MSG); return; }
    try {
      if (channel === "whatsapp") {
        const { data } = await api.post(`/boletos/${b.id}/whatsapp-share-link`);
        // Ajuste complementar, item 2 — PIX integrado já tem o QR pronto
        // (pix_qr_code_base64, o MESMO exibido no modal "Ver PIX") mesmo
        // aqui na listagem: tenta anexar junto da mensagem pelo mesmo
        // mecanismo dos modais, com o mesmo fallback sem download
        // automático. Boleto/PIX por chave (sem imagem pronta na linha)
        // seguem só com o texto, como sempre.
        if (b.pix_qr_code_base64) {
          const result = await shareQrToWhatsapp({ base64Png: b.pix_qr_code_base64, waLink: data.wa_link, filename: qrFilenameFor(b) });
          if (result.shared) toast.success("QR Code e mensagem compartilhados.");
          else if (result.cancelled) { /* usuário cancelou a folha de compartilhamento */ }
          else if (result.fallback && result.reason === "sem-suporte-anexo") toast.info('Seu navegador não permite anexar automaticamente o QR Code ao WhatsApp. WhatsApp aberto com a mensagem — abra "Ver PIX" para copiar ou baixar a imagem manualmente.', { duration: 8000 });
          else toast.success("WhatsApp aberto com a mensagem pronta — confirme o envio por lá");
        } else {
          window.open(data.wa_link, "_blank", "noopener,noreferrer");
          toast.success("WhatsApp aberto com a mensagem pronta — confirme o envio por lá");
        }
      } else {
        const { data } = await api.post(`/boletos/${b.id}/email-share`);
        if (data.sent) toast.success("Boleto enviado por e-mail ao cliente");
        else toast.info(data.reason || "Não foi possível enviar por e-mail");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Auditoria da experiência de boleto real Mercado Pago, item "LINHA
  // DIGITÁVEL" — copiar o valor completo (nunca truncado) com feedback
  // visível; e nomenclatura correta quando só há código de barras
  // (barcode_content) sem linha digitável bancária (digitable_line).
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiada`);
    } catch {
      toast.error(`Não foi possível copiar ${label.toLowerCase()}`);
    }
  };

  const [receiptsOpen, setReceiptsOpen] = useState(null);
  const [receiptsList, setReceiptsList] = useState([]);
  const openReceipts = async (i) => {
    try { const { data } = await api.get(`/invoices/${i.id}/receipts`); setReceiptsList(data); setReceiptsOpen(i); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const dlReceipt = (i, r) => {
    const url = `${API_BASE}/invoices/${i.id}/receipts/${r.id}/download`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" })
      .then(res => res.blob()).then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = r.file_name || "comprovante"; a.click();
      }).catch(() => toast.error("Falha ao baixar"));
  };

  // Etapa 4D (item 9) — alvo explícito opcional (boleto_id), mesmo
  // contrato da Etapa 4C. Faturas não-mistas continuam chamando sem
  // corpo, como sempre.
  const approveReceipt = async (i, r, boletoId) => {
    try {
      const body = boletoId ? { boleto_id: boletoId } : undefined;
      const { data } = await api.post(`/invoices/${i.id}/receipts/${r.id}/approve`, body);
      toast.success(data.boleto_paid ? "Comprovante aprovado e boleto baixado" : "Comprovante aprovado");
      const { data: updated } = await api.get(`/invoices/${i.id}/receipts`);
      setReceiptsList(updated);
      setApproveTargetByReceipt(s => { const n = { ...s }; delete n[r.id]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Item 9 do pedido — fatura mista com mais de uma cobrança elegível
  // (em_aberto/vencido) exige que o admin escolha EXPLICITAMENTE qual
  // cobrança o comprovante quita (o backend já rejeita com 409 se nada
  // for informado nesse caso, desde a Etapa 4C). `boletos` já vem
  // carregado por load() — nenhuma chamada nova.
  const [approveTargetByReceipt, setApproveTargetByReceipt] = useState({});
  const receiptEligibleCharges = (i) => boletos.filter(b => b.invoice_id === i.id && ["em_aberto", "vencido"].includes(b.status));
  const chargeLabel = (b) => {
    const valor = money(b.valor);
    if (b.tipo_cobranca === "entrada") return `Entrada — ${valor}`;
    // Delta Parcelamento via PIX — mesma numeração de chargeTitle acima.
    if (!b.component_id && b.parcela_numero) {
      return `${b.instrumento_tipo === "pix" ? "PIX" : "Boleto"} ${b.parcela_numero}/${b.parcela_total} — ${valor}`;
    }
    if (b.instrumento_tipo === "pix") return `PIX — ${valor}`;
    if (b.parcela_numero) return `Boleto ${b.parcela_numero}/${b.parcela_total} — ${valor}`;
    return `Boleto — ${valor}`;
  };
  // Ajuste de homologação (item 2 do pedido) — rótulo amigável de UMA
  // cobrança, SEM valor (usado na tabela/diálogos) — nunca expõe
  // component_id/comp1/comp2 (aqui ou em qualquer outro texto visível).
  // component_id só decide QUAL ramo do rótulo usar, nunca aparece
  // literalmente. Faturas legadas (sem parcela_numero/instrumento_tipo)
  // caem no fallback numero_documento, idêntico ao comportamento atual.
  const chargeTitle = (b) => {
    if (!b) return "";
    if (b.tipo_cobranca === "entrada") return "Entrada";
    // Delta Parcelamento via PIX — parcela do SALDO (nunca componente do
    // misto, que sempre tem component_id) numerada com o instrumento
    // certo: "PIX 1/N" ou "Boleto 1/N". Componente PIX do misto continua
    // "PIX" sem número (sempre 1/1, já era assim antes deste delta).
    if (!b.component_id && b.parcela_numero) {
      return `${b.instrumento_tipo === "pix" ? "PIX" : "Boleto"} ${b.parcela_numero}/${b.parcela_total}`;
    }
    if (b.instrumento_tipo === "pix") return "PIX";
    if (b.parcela_numero) return `Boleto ${b.parcela_numero}/${b.parcela_total}`;
    return b.numero_documento;
  };
  const invNumero = (invoiceId) => invoices.find(inv => inv.id === invoiceId)?.numero || "";
  const chargeRef = (b) => (b ? `${invNumero(b.invoice_id)} — ${chargeTitle(b)}` : "");

  const cliName = (id) => clients.find(c => c.id === id)?.razao_social || "-";
  // Item 1 do pedido — um documento modo="parcela_pendente" tem
  // status="em_aberto" no banco (mesmo campo/semântica de sempre — não
  // alterado), mas NUNCA foi emitido: mostrar "Em aberto" daria a
  // impressão de que já existe boleto/PIX real aguardando pagamento. Só
  // o RÓTULO/cor mudam aqui; depois da emissão o documento passa a ter
  // outro `modo` e volta a usar os status reais normalmente.
  const statusColor = (s, modo) => modo === "parcela_pendente"
    ? "bg-slate-100 text-slate-500"
    : ({ em_aberto: "bg-blue-100 text-blue-800", pago: "bg-emerald-100 text-emerald-800", vencido: "bg-red-100 text-red-800", cancelado: "bg-slate-200 text-slate-700" }[s] || "bg-slate-100");
  // PIX por chave nunca tem baixa automática/webhook — "Em aberto" sozinho
  // sugeriria uma consulta de status que não existe aqui; "Aguardando
  // confirmação" deixa claro que a baixa é sempre manual.
  const statusPt = (s, modo) => {
    if (modo === "parcela_pendente") return "A emitir";
    if (modo === "pix_chave" && s === "em_aberto") return "Aguardando confirmação";
    return ({ em_aberto: "Em aberto", pago: "Pago", vencido: "Vencido", cancelado: "Cancelado" }[s] || s);
  };

  // Item 4 do pedido — o MESMO filtro de Cliente (e período, quando
  // aplicável) usado na busca de fichas elegíveis agora também controla
  // Faturamentos e Cobranças — reaproveita `cliente`/`start`/`end`, nunca
  // um segundo estado de filtro. "__all__"/vazio = mostra tudo (limpar
  // filtros já reseta os três para esse estado).
  const dentroDoPeriodo = (isoDate) => {
    if (!isoDate) return true;
    const d = isoDate.slice(0, 10);
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };
  const filtraPorCliente = (clienteId) => cliente === "__all__" || clienteId === cliente;
  // Item 2 do pedido — Ativos (padrão)/Cancelados/Todos, aplicado da mesma
  // forma a Faturamentos e Cobranças (nunca dois filtros divergentes) — E
  // aos cards de acompanhamento abaixo, para nunca reabrir a regressão já
  // corrigida (card e tabela sempre precisam contar o MESMO universo).
  const filtraPorSituacao = (status) => {
    if (statusFiltro === "todos") return true;
    if (statusFiltro === "cancelados") return status === "cancelado";
    return status !== "cancelado";
  };
  const invoicesFiltrados = invoices
    .filter(i => filtraPorSituacao(i.status))
    .filter(i => filtraPorCliente(i.cliente_id) && dentroDoPeriodo(i.created_at));
  // Item 1 do pedido — uma cobrança só existe na visão operacional depois
  // de EMITIDA: `modo="parcela_pendente"` é só o planejamento interno
  // (parcela/entrada/componente do misto ainda sem instrumento real no
  // provider — preservado no banco, nunca apagado) e nunca deve aparecer
  // aqui como se já fosse uma cobrança de verdade. A tabela de
  // Faturamentos continua usando `boletos` (não filtrado) para decidir
  // "Emitir parcelas"/"Emitir cobrança" — só esta view operacional muda.
  //
  // Cards de acompanhamento (delta 2026-09-16) — contagens sobre o MESMO
  // universo que a tabela de Cobranças exibe (boletosVisiveis: emitidos +
  // dentro da situação escolhida), nunca um universo diferente — correção
  // de regressão pré-produção original: "Faturados" precisa bater com a
  // tabela que ele mesmo abre ao ser clicado.
  const boletosOperacionais = boletos.filter(b => b.modo !== "parcela_pendente");
  const boletosVisiveis = boletosOperacionais.filter(b => filtraPorSituacao(b.status));
  const boletosVencidos = boletosVisiveis.filter(b => b.status === "vencido").length;
  const boletosPagos = boletosVisiveis.filter(b => b.status === "pago").length;
  const boletosEmAberto = boletosVisiveis.filter(b => b.status === "em_aberto").length;
  const boletosFiltrados = boletosVisiveis
    .filter(b => !boletoCardFilter || b.status === boletoCardFilter)
    .filter(b => filtraPorCliente(b.cliente_id) && dentroDoPeriodo(b.vencimento));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-bold">Faturamento & Cobrança</h1>
        <p className="text-slate-500 text-sm">Selecione fichas concluídas para faturar e gerar boleto.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard
          icon={Hourglass} label="Aguardando faturamento" value={tickets.length} pct={null} color="amber"
          active={false} onClick={() => {}} testId="fat-card-aguardando-faturamento"
        />
        <SummaryCard
          icon={FileText} label="Faturados" value={boletosVisiveis.length} pct={null} color="indigo"
          active={boletoCardFilter === null} onClick={() => setBoletoCardFilter(null)} testId="fat-card-faturados"
        />
        <SummaryCard
          icon={XCircle} label="Vencidos" value={boletosVencidos} pct={null} color="red"
          active={boletoCardFilter === "vencido"} onClick={() => setBoletoCardFilter("vencido")} testId="fat-card-vencidos"
        />
        <SummaryCard
          icon={CheckCircle2} label="Pagos" value={boletosPagos} pct={null} color="emerald"
          active={boletoCardFilter === "pago"} onClick={() => setBoletoCardFilter("pago")} testId="fat-card-pagos"
        />
        <SummaryCard
          icon={Clock} label="Em aberto" value={boletosEmAberto} pct={null} color="blue"
          active={boletoCardFilter === "em_aberto"} onClick={() => setBoletoCardFilter("em_aberto")} testId="fat-card-em-aberto"
        />
      </div>

      <Card className="p-5 rounded-2xl shadow-sm space-y-3">
        {/* Item 12 do pedido — filtros recolhíveis, mesmo padrão de
            Fichas.jsx: painel começa fechado; Faturamentos/Cobranças/fichas
            elegíveis já aparecem sozinhos (buscarElegiveis roda no mount),
            sem exigir abrir o painel nem preencher nada antes. */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-heading font-semibold text-slate-900">Fichas aguardando faturamento</div>
          <Button variant={filtrosAtivos ? "default" : "outline"} onClick={() => setFiltersOpen(v => !v)} data-testid="fat-filtros-toggle">
            <Filter size={16} /> Filtros{filtrosAtivos ? " •" : ""}
          </Button>
        </div>
        {filtersOpen && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end pt-2 border-t border-slate-100">
            <div className="md:col-span-3">
              <Label className="text-xs text-slate-500">Cliente</Label>
              <Select value={cliente} onValueChange={setCliente}>
                <SelectTrigger data-testid="fat-cliente"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os clientes</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label className="text-xs text-slate-500">Período de</Label><Input type="date" value={start} onChange={e => setStart(e.target.value)} /></div>
            <div className="md:col-span-2"><Label className="text-xs text-slate-500">Período até</Label><Input type="date" value={end} onChange={e => setEnd(e.target.value)} /></div>
            <div className="md:col-span-3">
              {/* Item 2 do pedido — Ativos/Cancelados/Todos, mesmo filtro para
                  Faturamentos e Cobranças abaixo. */}
              <Label className="text-xs text-slate-500">Situação</Label>
              <Select value={statusFiltro} onValueChange={setStatusFiltro}>
                <SelectTrigger data-testid="fat-situacao"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativos">Ativos</SelectItem>
                  <SelectItem value="cancelados">Cancelados</SelectItem>
                  <SelectItem value="todos">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={buscarElegiveis} data-testid="fat-buscar">
                <Search size={16} /> Filtrar
              </Button>
              <Button variant="outline" size="icon" onClick={limparFiltros} title="Limpar filtros" data-testid="fat-limpar-filtros">
                <X size={16} />
              </Button>
            </div>
          </div>
        )}

        {tickets.length > 0 && (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-slate-600">
                  <tr>
                    <th className="py-2 px-3">
                      <input
                        type="checkbox"
                        ref={headerCheckboxRef}
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        title={allSelected ? "Desmarcar todos" : "Selecionar todos"}
                        data-testid="fat-selecionar-todos"
                      />
                    </th>
                    <th>Ficha</th><th>Cliente</th><th>Data</th><th>Serviço</th><th>Horas fat.</th><th>Valor hora</th><th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} className="border-t border-slate-100">
                      <td className="py-2 px-3"><input type="checkbox" checked={!!selected[t.id]} onChange={e => setSelected({ ...selected, [t.id]: e.target.checked })} data-testid={`sel-${t.numero}`} /></td>
                      <td>#{t.numero}</td><td>{cliName(t.cliente_id)}</td><td>{fmtDate(t.data_servico)}</td><td>{t.tipo_servico}</td>
                      <td>{fmtHours(t.horas_faturaveis)}</td><td>{money(t.valor_hora)}</td><td>{money(t.valor_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex flex-wrap justify-between items-center gap-4">
              <div className="flex gap-6 text-sm">
                <div><span className="text-slate-500">Fichas: </span><b>{selectedTickets.length}</b></div>
                <div><span className="text-slate-500">Horas: </span><b>{fmtHours(totalHoras)}</b></div>
                <div><span className="text-slate-500">Total: </span><b className="text-primary font-heading">{money(totalValor)}</b></div>
              </div>
              <div className="flex gap-2">
                {podeAglutinar && (
                  <Button variant="outline" onClick={() => setAglutinarOpen(true)} data-testid="fat-aglutinar">
                    <Layers size={16} /> {selectedTickets.length >= 2 ? "Aglutinar selecionadas" : "Forma de pagamento"}
                  </Button>
                )}
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={gerarFaturamento} data-testid="fat-gerar" title="Cria só a fatura, sem escolher forma de cobrança agora"><Receipt size={16} /> Faturamento direto</Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <AglutinarDialog
        open={aglutinarOpen}
        onOpenChange={setAglutinarOpen}
        clienteNome={aglutClienteId ? cliName(aglutClienteId) : ""}
        clienteId={aglutClienteId}
        tickets={selectedTickets}
        onCreated={() => { setTickets([]); setSelected({}); load(); }}
        temPixIntegrado={defaultPaymentAccounts.length > 0}
        temPixChave={activePixKeyAccounts.length > 0}
      />

      <Card className="p-0 overflow-hidden rounded-2xl shadow-sm">
        <div className="p-5 pb-4 flex items-center gap-3 border-b border-slate-100">
          <div className="rounded-full bg-blue-100 p-2 text-blue-700 shrink-0"><FileText size={18} /></div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Faturamentos</div>
            <div className="text-xs text-slate-500">Fichas concluídas prontas para faturar ou já faturadas.</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr><th className="py-3 px-4">Nº</th><th>Cliente</th><th>Data</th><th>Horas</th><th>Valor</th><th>Status</th><th className="text-right pr-4">Ações</th></tr>
            </thead>
            <tbody>
              {invoicesFiltrados.map(i => {
                const invBoletos = boletos.filter(b => b.invoice_id === i.id);
                const has = invBoletos.find(b => b.status !== "cancelado");
                const parcelado = i.forma_pagamento === "parcelado";
                const misto = i.forma_pagamento === "misto";
                const parcelas = invBoletos.filter(b => b.parcela_numero && b.status !== "cancelado")
                  .sort((a, b) => a.parcela_numero - b.parcela_numero);
                // Ajuste complementar, item 1 — PIX por chave nunca tem
                // external_charge_id (não existe provedor externo; a
                // emissão grava pix_txid, mesmo critério já usado em
                // parcelasPendentesDaFatura acima). Checar só
                // external_charge_id fazia uma parcela de PIX por chave já
                // emitida continuar aparecendo como pendente para sempre.
                const parcelasPendentes = parcelas.filter(b => !b.external_charge_id && !b.pix_txid && b.status === "em_aberto");
                // Delta Parcelamento via PIX — "Forma da cobrança" do
                // parcelamento é a MESMA para todas as N parcelas do saldo
                // (escolhida uma vez na criação); rótulo do botão único
                // reflete isso, nunca fica genérico "Emitir parcelas"
                // quando na verdade é PIX (evita confundir com boleto).
                const parceladoPix = parcelas[0]?.instrumento_tipo === "pix";
                // Etapa 4D (item 6) — estado de CADA componente do misto,
                // separado (comp1=PIX sempre 1 doc, comp2=BOLETO 1..N docs
                // não cancelados) — nunca um botão único ambíguo.
                const mistoPix = invBoletos.find(b => b.component_id === "comp1" && b.status !== "cancelado");
                const mistoBoletoDocs = invBoletos.filter(b => b.component_id === "comp2" && b.status !== "cancelado");
                const mistoBoletoPendentes = mistoBoletoDocs.filter(b => !b.external_charge_id && !b.pix_txid && b.status === "em_aberto");
                // Item 1 do pedido (ajustes finais) — true se QUALQUER
                // componente ainda tiver algo pendente de emitir; a ação
                // única "Emitir cobrança" cobre PIX e boleto de uma vez.
                // Ajuste complementar, item 1 — mesmo cuidado com PIX por
                // chave do componente PIX do misto (comp1).
                const mistoPendente = misto && ((mistoPix && !mistoPix.external_charge_id && !mistoPix.pix_txid) || mistoBoletoPendentes.length > 0);
                const statusCls = i.status === "pago" ? "bg-emerald-100 text-emerald-800"
                  : i.status === "parcialmente_pago" ? "bg-amber-100 text-amber-800"
                  : i.status === "cancelado" ? "bg-slate-100 text-slate-600"
                  : "bg-blue-100 text-blue-800";
                const statusLabel = i.status === "parcialmente_pago" ? "parcialmente pago" : i.status;
                return (
                  <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium">
                      {i.numero}
                      {(i.ticket_ids?.length || 0) > 1 && <Badge className="ml-1 rounded-full bg-slate-100 text-slate-600">{i.ticket_ids.length} fichas</Badge>}
                      {parcelado && <Badge className="ml-1 rounded-full bg-indigo-100 text-indigo-800">{i.parcelas_total}x</Badge>}
                      {misto && <Badge className="ml-1 rounded-full bg-fuchsia-100 text-fuchsia-800">Misto</Badge>}
                    </td>
                    <td>{cliName(i.cliente_id)}</td>
                    <td>{fmtDate(i.created_at)}</td>
                    <td>{fmtHours(i.horas_total)}</td>
                    <td>{money(i.valor_total)}</td>
                    <td>
                      <Badge className={`rounded-full ${statusCls}`} title={i.status === "cancelado" && i.cancel_reason ? `Motivo: ${i.cancel_reason}` : undefined}>{statusLabel}</Badge>
                      {i.status === "cancelado" && i.cancel_reason && (
                        <div className="text-xs text-slate-400 italic mt-0.5 max-w-[180px] truncate" title={i.cancel_reason} data-testid={`invoice-cancel-reason-${i.numero}`}>
                          "{i.cancel_reason}"
                        </div>
                      )}
                    </td>
                    <td className="pr-4">
                      {/* Ajuste visual — coluna de ações em slots de largura fixa
                          (mesma ordem/handlers de sempre): cada ícone ocupa uma
                          área clicável quadrada consistente e os botões/badges
                          de status (Emitir parcelas/Parcelas emitidas/Gerar
                          boleto/Boleto emitido/Gerar PIX) ficam em slots
                          próprios, sem empurrar os ícones à esquerda. */}
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <div className="w-8 flex justify-center shrink-0">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openReceipts(i)} data-testid={`inv-receipts-${i.numero}`} title="Comprovantes">
                            <Paperclip size={14} />
                          </Button>
                        </div>
                        <div className="w-8 flex justify-center shrink-0">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => dlInvoice(i)} data-testid={`inv-pdf-${i.numero}`} title="Baixar faturamento"><FileDown size={14} /></Button>
                        </div>
                        <div className="w-8 flex justify-center shrink-0">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => resendInvoice(i)} data-testid={`inv-mail-${i.numero}`} title={tenantEmailConfigured === false ? EMAIL_NOT_CONFIGURED_MSG : "Reenviar e-mail"}><Mail size={14} className={i.email_sent_at ? "text-emerald-600" : ""} /></Button>
                        </div>
                        {/* NFS-e Nacional, Fase 1 — leva para Notas Fiscais
                            (preparar/validar/testar; nunca "Emitir" ainda,
                            ver docs/NFSE_NACIONAL.md). Só aparece se o
                            módulo estiver habilitado para a empresa. */}
                        <div className="w-8 flex justify-center shrink-0">
                          {nfseAtiva && i.status !== "cancelado" && (
                            <Link to="/notas-fiscais" data-testid={`inv-nfse-${i.numero}`}>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title={nfseByInvoice[i.id] ? `NFS-e: ${nfseByInvoice[i.id].status}` : "Preparar NFS-e"}>
                                <FileBadge2 size={14} className={nfseByInvoice[i.id] ? "text-indigo-600" : ""} />
                              </Button>
                            </Link>
                          )}
                        </div>
                        <div className="w-8 flex justify-center shrink-0">
                          {user.role === "admin" && i.status !== "cancelado" && (
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => setCancelInvoiceOpen(i)} data-testid={`cancelar-faturamento-${i.numero}`} title="Cancelar faturamento">
                              <XCircle size={14} />
                            </Button>
                          )}
                        </div>
                        <div className="min-w-[150px] flex justify-end shrink-0">
                          {parcelado && parcelasPendentes.length > 0 && i.status !== "cancelado" && (
                            <Button size="sm" className="h-8" onClick={() => emitirParcelas(i)} data-testid={`emitir-parcelas-${i.numero}`}>
                              {parceladoPix ? `Emitir cobrança (${parcelasPendentes.length})` : `Emitir parcelas (${parcelasPendentes.length})`}
                            </Button>
                          )}
                          {parcelado && parcelasPendentes.length === 0 && parcelas.length > 0 && (
                            <Badge className="rounded-full bg-indigo-100 text-indigo-800">{parceladoPix ? "Cobrança emitida" : "Parcelas emitidas"}</Badge>
                          )}
                          {/* Item 1 do pedido (ajustes finais) — ação ÚNICA
                              "Emitir cobrança" dispara PIX + boleto do
                              misto numa só chamada da UI (internamente 2
                              requisições, cada uma com component_id
                              explícito — fluxo da Etapa 4C inalterado). */}
                          {mistoPendente && i.status !== "cancelado" && (
                            <Button size="sm" className="h-8"
                              disabled={emittingComponent === `${i.id}:cobranca`}
                              onClick={() => emitirCobrancaMista(i)} data-testid={`emitir-cobranca-misto-${i.numero}`}>
                              {emittingComponent === `${i.id}:cobranca` ? "Emitindo..." : "Emitir cobrança"}
                            </Button>
                          )}
                          {misto && !mistoPendente && (mistoPix || mistoBoletoDocs.length > 0) && (
                            <Badge className="rounded-full bg-indigo-100 text-indigo-800">Cobrança emitida</Badge>
                          )}
                          {!parcelado && !misto && !has && <Button size="sm" className="h-8" onClick={() => openBoletoDialog(i)} data-testid={`gerar-boleto-${i.numero}`}>Gerar boleto</Button>}
                          {!parcelado && !misto && has && <Badge className="rounded-full bg-indigo-100 text-indigo-800">Boleto emitido</Badge>}
                        </div>
                        <div className="min-w-[150px] flex justify-end shrink-0">
                          {/* Item 3 — entrada única "Gerar PIX": pergunta só
                              quando as duas opções existem; senão segue
                              direto para o único fluxo disponível; sem
                              nenhuma opção, orienta a configurar. */}
                          {!parcelado && !misto && !has && (
                            <Button size="sm" variant="outline" className="h-8 border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => abrirGerarPix(i)} data-testid={`gerar-pix-${i.numero}`}>
                              <QrCode size={14} /> Gerar PIX
                            </Button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {invoicesFiltrados.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Nenhum faturamento</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden rounded-2xl shadow-sm">
        <div className="p-5 pb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-indigo-100 p-2 text-indigo-700 shrink-0"><Barcode size={18} /></div>
            <div>
              <div className="font-heading font-semibold text-slate-900">Cobranças</div>
              <div className="text-xs text-slate-500">Boleto, PIX e parcelas já emitidas dos faturamentos.</div>
            </div>
          </div>
          {boletosSincronizaveis > 0 && (
            <Button
              size="sm" variant="outline"
              onClick={sincronizarTodos}
              disabled={syncingAll}
              data-testid="sync-all-boletos"
            >
              {syncingAll ? "Sincronizando..." : "Sincronizar todos"}
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr><th className="py-3 px-4">Cobrança</th><th>Cliente</th><th>Vencimento</th><th>Valor</th><th>Status</th><th>Origem</th><th>Linha digitável</th><th className="text-right pr-4">Ações</th></tr>
            </thead>
            <tbody>
              {boletosFiltrados.map(b => (
                <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium">{chargeTitle(b)}</td>
                  <td>{cliName(b.cliente_id)}</td>
                  <td>{fmtDate(b.vencimento)}</td>
                  <td>{money(b.valor)}</td>
                  <td>
                    <Badge className={`rounded-full ${statusColor(b.status, b.modo)}`} title={b.status === "cancelado" && b.cancel_reason ? `Motivo: ${b.cancel_reason}` : undefined}>{statusPt(b.status, b.modo)}</Badge>
                    {b.status === "cancelado" && b.cancel_reason && (
                      <div className="text-xs text-slate-400 italic mt-0.5 max-w-[160px] truncate" title={b.cancel_reason} data-testid={`boleto-cancel-reason-${b.numero_documento}`}>
                        "{b.cancel_reason}"
                      </div>
                    )}
                  </td>
                  <td>
                    {b.modo === "mercado_pago" ? (
                      <div className="flex items-center gap-1">
                        <Badge className="rounded-full bg-blue-100 text-blue-800">Mercado Pago</Badge>
                        {b.environment === "test" && <Badge className="rounded-full bg-amber-100 text-amber-800">Ambiente de teste</Badge>}
                      </div>
                    ) : b.modo === "asaas" ? (
                      // Item "TELA DE COBRANÇA" — provedor sempre explícito;
                      // quando Sandbox, o rótulo já deixa isso embutido
                      // ("Asaas — Teste") para nunca confundir com produção.
                      <div className="flex items-center gap-1">
                        <Badge className="rounded-full bg-teal-100 text-teal-800">{b.environment === "test" ? "Asaas — Teste" : "Asaas"}</Badge>
                      </div>
                    ) : b.modo === "pix_chave" ? (
                      <Badge className="rounded-full bg-violet-100 text-violet-800">PIX por chave</Badge>
                    ) : b.modo === "parcela_pendente" ? (
                      <Badge className="rounded-full bg-slate-100 text-slate-600">Parcela — a emitir</Badge>
                    ) : (
                      <Badge className="rounded-full bg-slate-100 text-slate-600">Ilustrativo</Badge>
                    )}
                  </td>
                  <td className="font-mono text-xs">
                    {b.modo === "pix_chave" ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-violet-700 hover:underline"
                        onClick={() => setPixChaveModalData(b)}
                        data-testid={`ver-qr-pix-chave-${b.numero_documento}`}
                      >
                        <QrCode size={12} /> Ver QR
                      </button>
                    ) : b.instrumento_tipo === "pix" ? (
                      "PIX"
                    ) : b.linha_digitavel ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="max-w-[220px] truncate" title={b.linha_digitavel}>{b.linha_digitavel}</span>
                        <button
                          type="button"
                          title="Copiar linha digitável"
                          onClick={() => copyToClipboard(b.linha_digitavel, "Linha digitável")}
                          className="text-slate-400 hover:text-slate-700 shrink-0"
                          data-testid={`copiar-linha-digitavel-${b.numero_documento}`}
                        >
                          <Copy size={12} />
                        </button>
                      </span>
                    ) : b.codigo_barras ? (
                      // Item "LINHA DIGITÁVEL" — o Mercado Pago pode devolver
                      // só o código de barras (barcode_content), sem a linha
                      // digitável bancária (digitable_line); nomenclatura
                      // correta em vez de rotular como "linha digitável".
                      <span className="inline-flex items-center gap-1">
                        <span className="text-slate-500">Cód. de barras:</span>
                        <span className="max-w-[180px] truncate" title={b.codigo_barras}>{b.codigo_barras}</span>
                        <button
                          type="button"
                          title="Copiar código de barras"
                          onClick={() => copyToClipboard(b.codigo_barras, "Código de barras")}
                          className="text-slate-400 hover:text-slate-700 shrink-0"
                          data-testid={`copiar-codigo-barras-${b.numero_documento}`}
                        >
                          <Copy size={12} />
                        </button>
                      </span>
                    ) : "-"}
                  </td>
                  <td className="text-right pr-4 space-x-1">
                    {b.status === "em_aberto" && b.modo === "pix_chave" && (
                      <Button size="sm" variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => openConfirmRecebimento(b)} data-testid={`confirmar-recebimento-${b.numero_documento}`}>
                        <CheckCircle2 size={14} /> Confirmar recebimento
                      </Button>
                    )}
                    {/* Item 3 do pedido — nunca oferecer baixa manual de uma
                        cobrança que ainda não foi emitida (modo="parcela_pendente"):
                        não existe boleto/PIX real para "confirmar recebimento" dele. */}
                    {b.status === "em_aberto" && b.modo !== "pix_chave" && b.modo !== "parcela_pendente" && <Button size="sm" variant="outline" onClick={() => pay(b)} data-testid={`pay-${b.numero_documento}`}><CheckCircle2 size={14} /> Baixar</Button>}
                    {/* Correção PIX Asaas — "Ver PIX" abre QR/copia-e-cola
                        REAIS do provedor integrado (Mercado Pago/Asaas);
                        NUNCA confundido com "Gerar PIX por chave" acima
                        (modo="pix_chave", já tratado à parte). */}
                    {(b.modo === "mercado_pago" || b.modo === "asaas") && b.instrumento_tipo === "pix" && b.status !== "cancelado" && (
                      <Button size="sm" variant="outline" onClick={() => setIntegratedPixModalData(b)} data-testid={`ver-pix-${b.numero_documento}`}>
                        <QrCode size={14} /> Ver PIX
                      </Button>
                    )}
                    {/* Auditoria da experiência de boleto real, item "INTERFACE"
                        — abre o ticket_url REAL do Mercado Pago (nunca uma URL
                        construída pelo LOOTUS) em nova aba; só aparece quando
                        já existe (nunca um botão quebrado) — enquanto isso,
                        "Sincronizar" (abaixo) é o caminho para tentar obtê-lo. */}
                    {b.boleto_url && b.status !== "cancelado" && (
                      <Button size="sm" variant="outline" onClick={() => window.open(b.boleto_url, "_blank", "noopener,noreferrer")}
                              title="Abrir boleto" data-testid={`abrir-boleto-${b.numero_documento}`}>
                        <ExternalLink size={14} /> Abrir boleto
                      </Button>
                    )}
                    {(b.modo === "mercado_pago" || b.modo === "asaas") && b.status !== "cancelado" && (
                      <Button size="sm" variant="outline" onClick={() => sincronizarBoleto(b)} disabled={syncingBoleto === b.id} data-testid={`sincronizar-${b.numero_documento}`}>
                        {syncingBoleto === b.id ? "Sincronizando..." : "Sincronizar"}
                      </Button>
                    )}
                    {/* Item 3 do pedido — nada real para compartilhar antes da
                        emissão (sem boleto_url/QR/linha digitável ainda). */}
                    {b.status !== "cancelado" && b.modo !== "parcela_pendente" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" title="Compartilhar" data-testid={`boleto-compartilhar-${b.numero_documento}`}>
                            <Share2 size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => shareBoleto(b, "email")} data-testid={`boleto-compartilhar-email-${b.numero_documento}`}>
                            <Mail size={14} className="mr-2" /> E-mail
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => shareBoleto(b, "whatsapp")} data-testid={`boleto-compartilhar-whatsapp-${b.numero_documento}`}>
                            <MessageCircle size={14} className="mr-2" /> WhatsApp
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {b.status !== "cancelado" && b.status !== "pago" && <Button size="sm" variant="ghost" onClick={() => { setCancelBoletoOpen(b); setCancelBoletoReason(""); }} data-testid={`cancelar-boleto-${b.numero_documento}`}><Ban size={14} /></Button>}
                    {/* Ajustes finais da Homologação V1, item 3 — "Anular baixa" só
                        aparece para baixa MANUAL (inclui aprovação de comprovante).
                        Baixa por conciliação bancária nunca oferece a ação aqui —
                        o backend também rejeitaria uma tentativa direta pela API. */}
                    {user.role === "admin" && b.status === "pago" && !b.baixa_via_reconciliacao && !b.baixa_via_provider && (
                      <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50" onClick={() => setReverseBoletoOpen(b)} data-testid={`anular-baixa-${b.numero_documento}`} title="Anular baixa">
                        <Ban size={14} /> Anular baixa
                      </Button>
                    )}
                    {b.status === "pago" && b.baixa_via_reconciliacao && (
                      <span className="text-xs text-slate-400 italic" title="Baixa realizada por conciliação bancária. A baixa não pode ser anulada manualmente.">
                        Baixa por conciliação
                      </span>
                    )}
                    {b.status === "pago" && b.baixa_via_provider && (
                      <span className="text-xs text-slate-400 italic" title={`Pagamento confirmado pelo ${b.modo === "asaas" ? "Asaas" : "Mercado Pago"}. A baixa não pode ser anulada manualmente.`}>
                        Confirmado pelo {b.modo === "asaas" ? "Asaas" : "Mercado Pago"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {/* Correção de regressão pré-produção — checava boletos.length
                  (universo total), mas as linhas acima vêm de
                  boletosFiltrados: com um card de status ativo e zero
                  resultados, a mensagem nunca aparecia (boletos.length
                  continuava > 0), deixando a tabela em branco sem
                  explicação. */}
              {boletosFiltrados.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">Nenhum boleto</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="p-4 text-xs text-slate-500 bg-amber-50 border-t border-amber-200">
          ⚠️ Boletos marcados <b>Ilustrativo</b> não representam cobranças bancárias reais.
          Boletos <b>Mercado Pago</b> e <b>Asaas</b> são emitidos na sua própria conta, configurada em Configurações → Bancos e Pagamentos.
          Cobranças <b>PIX por chave</b> são geradas localmente pela LOOTUS (sem integração bancária) — a baixa é sempre manual, feita em "Confirmar recebimento".
        </div>
      </Card>

      <Dialog open={!!boletoOpen} onOpenChange={v => !v && setBoletoOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{pixOnlyMode ? "Gerar PIX" : "Gerar boleto"} — {boletoOpen?.numero}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cliente</Label><Input disabled value={cliName(boletoOpen?.cliente_id)} /></div>
            <div><Label>Valor</Label><Input disabled value={money(boletoOpen?.valor_total)} /></div>
            <div><Label>Vencimento</Label><Input type="date" value={venc} onChange={e => setVenc(e.target.value)} data-testid="boleto-venc" /></div>
          </div>
          {defaultPaymentAccounts.length > 1 && (
            <div>
              <Label>Provedor</Label>
              <Select value={boletoProviderId} onValueChange={setBoletoProviderId}>
                <SelectTrigger data-testid="boleto-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {defaultPaymentAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.display_name} ({a.provider === "asaas" ? "Asaas" : "Mercado Pago"})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {activeBoletoAccount ? (
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
              Emissão via <b>{activeBoletoAccount.display_name}</b> ({activeBoletoAccount.provider === "asaas" ? "Asaas" : "Mercado Pago"})
              {activeBoletoAccount.environment === "test" && <Badge className="ml-2 bg-amber-100 text-amber-800">Ambiente de teste</Badge>}
            </div>
          ) : (
            // Item 5 do ajuste de UX — sem conta de recebimento configurada, o
            // Faturamento nunca oferece cobrança simulada como alternativa
            // silenciosa: só mensagem amigável + link para configurar.
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="sem-conta-pagamento-aviso">
              Configure uma conta de recebimento para emitir boletos e PIX.{" "}
              <Link to="/configuracoes" className="underline font-medium" onClick={() => setBoletoOpen(null)}>Configurações → Bancos e Pagamentos</Link>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setBoletoOpen(null)}>Cancelar</Button>
            {activeBoletoAccount && (
              <>
                <Button variant="outline" disabled={generatingInstrument} onClick={() => gerarInstrumentoReal("pix")} data-testid="pix-provider-confirmar">
                  {generatingInstrument ? "Gerando..." : "Gerar PIX"}
                </Button>
                {/* Item 3.2 — quando aberto via "Gerar PIX" com só o
                    provedor integrado disponível, segue direto (só o botão
                    de PIX); "Gerar boleto" só aparece quando aberto pelo
                    fluxo normal de boleto, nunca duplicando o diálogo. */}
                {!pixOnlyMode && (
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" disabled={generatingInstrument} onClick={() => gerarInstrumentoReal("boleto")} data-testid="boleto-provider-confirmar">
                    {generatingInstrument ? "Gerando..." : "Gerar boleto"}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item 3.1 — escolha do tipo de PIX, só quando Asaas/Mercado Pago E
          chave cadastrada estão configurados ao mesmo tempo (abrirGerarPix
          decide isso); cada opção só entra no fluxo já existente
          correspondente, nunca uma terceira implementação de geração. */}
      <Dialog open={!!pixChoiceOpen} onOpenChange={v => !v && setPixChoiceOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Como deseja receber este PIX?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <button
              type="button"
              className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-colors"
              onClick={() => { const inv = pixChoiceOpen; setPixChoiceOpen(null); openBoletoDialog(inv, { pixOnly: true }); }}
              data-testid="pix-escolha-integrado"
            >
              <div className="font-heading font-semibold text-slate-900">PIX via {integradoProviderLabel}</div>
              <div className="text-sm text-slate-500 mt-1">Cobrança PIX integrada. Confirmação automática conforme integração/webhook.</div>
            </button>
            <button
              type="button"
              className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-violet-400 hover:bg-violet-50 transition-colors"
              onClick={() => { const inv = pixChoiceOpen; setPixChoiceOpen(null); openPixChaveDialog(inv); }}
              data-testid="pix-escolha-chave"
            >
              <div className="font-heading font-semibold text-slate-900">PIX por chave cadastrada</div>
              <div className="text-sm text-slate-500 mt-1">Utiliza a chave PIX configurada pela empresa. Confirmação manual ou por conciliação, conforme fluxo já existente.</div>
            </button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPixChoiceOpen(null)}>Cancelar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIX por chave — dialog SEPARADO do "Gerar boleto" acima (Mercado
          Pago): nunca a mesma ação, nunca o mesmo texto. "Data de
          referência" é só informativa — nunca uma expiração real do QR. */}
      <Dialog open={!!pixChaveOpen} onOpenChange={v => !v && setPixChaveOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode size={18} className="text-violet-600" /> Gerar PIX por chave — {pixChaveOpen?.numero}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cliente</Label><Input disabled value={cliName(pixChaveOpen?.cliente_id)} /></div>
            <div><Label>Valor</Label><Input disabled value={money(pixChaveOpen?.valor_total)} /></div>
            <div>
              <Label>Data de referência</Label>
              <Input type="date" value={pixChaveVenc} onChange={e => setPixChaveVenc(e.target.value)} data-testid="pix-chave-venc" />
            </div>
            {activePixKeyAccounts.length > 1 && (
              <div>
                <Label>Chave Pix</Label>
                <Select value={pixChaveAccountId || "__default__"} onValueChange={v => setPixChaveAccountId(v === "__default__" ? "" : v)}>
                  <SelectTrigger data-testid="pix-chave-conta"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Padrão</SelectItem>
                    {activePixKeyAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="text-sm text-violet-800 bg-violet-50 border border-violet-200 rounded-md p-3">
            Este QR Code Pix é gerado localmente pela LOOTUS — não exige integração bancária e não expira automaticamente como um QR emitido por um banco.
            A data de referência acima é só uma anotação interna; a confirmação do recebimento é sempre manual, em "Confirmar recebimento".
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPixChaveOpen(null)}>Cancelar</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" disabled={generatingPixChave} onClick={gerarPixChave} data-testid="pix-chave-confirmar">
              {generatingPixChave ? "Gerando..." : "Gerar PIX por chave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PixChaveModal
        open={!!pixChaveModalData}
        onOpenChange={v => !v && setPixChaveModalData(null)}
        title={`PIX — ${invNumero(pixChaveModalData?.invoice_id)}`}
        valor={pixChaveModalData?.valor}
        recebedor={pixChaveModalData?.pix_key_snapshot?.holder_name}
        instituicao={pixChaveModalData?.pix_key_snapshot?.institution_name}
        brcode={pixChaveModalData?.pix_brcode}
        onCopy={copyToClipboard}
        onGetWhatsappLink={() => getBoletoWhatsappLink(pixChaveModalData)}
        onShareEmail={() => shareBoleto(pixChaveModalData, "email")}
        filename={qrFilenameFor(pixChaveModalData)}
      />

      <IntegratedPixModal
        open={!!integratedPixModalData}
        onOpenChange={v => !v && setIntegratedPixModalData(null)}
        title={`PIX — ${invNumero(integratedPixModalData?.invoice_id)}`}
        boleto={integratedPixModalData}
        onCopy={copyToClipboard}
        onGetWhatsappLink={() => getBoletoWhatsappLink(integratedPixModalData)}
        filename={qrFilenameFor(integratedPixModalData)}
      />

      <Dialog open={!!confirmRecebimentoOpen} onOpenChange={v => !v && setConfirmRecebimentoOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirmar recebimento — {chargeRef(confirmRecebimentoOpen)}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">PIX por chave não tem confirmação automática — registre aqui o que foi efetivamente recebido.</p>
          <div className="space-y-3">
            <div><Label>Valor recebido</Label><Input type="number" step="0.01" value={confirmRecebimentoForm.valor_recebido} onChange={e => setConfirmRecebimentoForm(f => ({ ...f, valor_recebido: e.target.value }))} data-testid="confirmar-recebimento-valor" /></div>
            <div><Label>Data do recebimento</Label><Input type="date" value={confirmRecebimentoForm.data_recebimento} onChange={e => setConfirmRecebimentoForm(f => ({ ...f, data_recebimento: e.target.value }))} data-testid="confirmar-recebimento-data" /></div>
            <div><Label>Observação (opcional)</Label><Textarea rows={3} value={confirmRecebimentoForm.observacao} onChange={e => setConfirmRecebimentoForm(f => ({ ...f, observacao: e.target.value }))} data-testid="confirmar-recebimento-obs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRecebimentoOpen(null)}>Cancelar</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" disabled={confirmingRecebimento} onClick={doConfirmRecebimento} data-testid="confirmar-recebimento-salvar">
              {confirmingRecebimento ? "Confirmando..." : "Confirmar recebimento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receiptsOpen} onOpenChange={v => !v && setReceiptsOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Paperclip size={18} /> Comprovantes — {receiptsOpen?.numero}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {receiptsList.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Nenhum comprovante anexado pelo cliente ainda.</div>}
            {(() => {
              // Item 9 do pedido — fatura mista com 2+ cobranças elegíveis
              // exige alvo explícito; não-mista (ou misto com só 1
              // elegível) preserva o comportamento atual (aprovar direto).
              const elegiveis = receiptsOpen ? receiptEligibleCharges(receiptsOpen) : [];
              const ambiguo = receiptsOpen?.forma_pagamento === "misto" && elegiveis.length > 1;
              return receiptsList.map(r => (
                <div key={r.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <div className="flex flex-wrap justify-between gap-2 text-sm">
                    <div>
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {r.file_name}
                        {r.approved_at && <Badge className="bg-emerald-100 text-emerald-800 text-xs">✓ Aprovado</Badge>}
                      </div>
                      <div className="text-xs text-slate-500">Enviado por <b>{r.payer_name}</b> em {new Date(r.uploaded_at).toLocaleString("pt-BR")}</div>
                      {r.note && <div className="text-xs text-slate-600 mt-1 italic">"{r.note}"</div>}
                      {r.approved_at && <div className="text-xs text-emerald-700 mt-1">Aprovado por {r.approved_by_name} em {new Date(r.approved_at).toLocaleString("pt-BR")}</div>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => dlReceipt(receiptsOpen, r)} data-testid={`dl-receipt-${r.id}`}><FileDown size={14} /> Baixar</Button>
                      {!r.approved_at && !ambiguo && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approveReceipt(receiptsOpen, r)} data-testid={`approve-receipt-${r.id}`}><CheckCircle2 size={14} /> Aprovar</Button>
                      )}
                    </div>
                  </div>
                  {!r.approved_at && ambiguo && (
                    <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5" data-testid={`approve-target-picker-${r.id}`}>
                      <div className="text-xs text-slate-500">Fatura com pagamento misto — selecione qual cobrança este comprovante quita:</div>
                      <div className="flex flex-wrap gap-1.5">
                        {elegiveis.map(b => (
                          <Button key={b.id} type="button" size="sm"
                            variant={approveTargetByReceipt[r.id] === b.id ? "default" : "outline"}
                            onClick={() => setApproveTargetByReceipt(s => ({ ...s, [r.id]: b.id }))}
                            data-testid={`approve-target-${r.id}-${b.id}`}>
                            {chargeLabel(b)}
                          </Button>
                        ))}
                      </div>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!approveTargetByReceipt[r.id]}
                        onClick={() => approveReceipt(receiptsOpen, r, approveTargetByReceipt[r.id])} data-testid={`approve-receipt-${r.id}`}>
                        <CheckCircle2 size={14} /> Aprovar
                      </Button>
                    </div>
                  )}
                </div>
              ));
            })()}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setReceiptsOpen(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelInvoiceOpen} onOpenChange={v => { if (!v) { setCancelInvoiceOpen(null); setCancelInvoiceReason(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><XCircle className="text-red-600" size={18} /> Cancelar faturamento — {cancelInvoiceOpen?.numero}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            As fichas incluídas voltam para "aguardando faturamento" e o boleto em aberto vinculado (se houver) é cancelado.
            Um faturamento já pago não pode ser cancelado por aqui — anule a baixa do boleto primeiro.
          </p>
          <div><Label>Motivo</Label><Textarea rows={4} value={cancelInvoiceReason} onChange={e => setCancelInvoiceReason(e.target.value)} data-testid="cancel-invoice-reason-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelInvoiceOpen(null); setCancelInvoiceReason(""); }}>Voltar</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={!cancelInvoiceReason.trim()} onClick={doCancelInvoice} data-testid="confirm-cancel-invoice">Cancelar faturamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Últimos ajustes antes da produção, item 1 — cancelamento de
          boleto/PIX individual exige justificativa (mesmo padrão do
          cancelamento de faturamento acima). */}
      <Dialog open={!!cancelBoletoOpen} onOpenChange={v => { if (!v) { setCancelBoletoOpen(null); setCancelBoletoReason(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="text-red-600" size={18} /> Cancelar cobrança — {chargeRef(cancelBoletoOpen)}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            {(cancelBoletoOpen?.modo === "mercado_pago" || cancelBoletoOpen?.modo === "asaas")
              ? "O cancelamento é confirmado no provedor quando possível — se não for possível confirmar, você será avisado (o cancelamento local não é desfeito nem escondido)."
              : "Esta cobrança ainda não foi emitida em nenhum provedor externo — o cancelamento é só local."}
          </p>
          <div><Label>Motivo</Label><Textarea rows={4} value={cancelBoletoReason} onChange={e => setCancelBoletoReason(e.target.value)} data-testid="cancel-boleto-reason-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelBoletoOpen(null); setCancelBoletoReason(""); }}>Voltar</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={!cancelBoletoReason.trim()} onClick={doCancelBoleto} data-testid="confirm-cancel-boleto">Cancelar cobrança</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reverseBoletoOpen} onOpenChange={v => { if (!v) { setReverseBoletoOpen(null); setReverseBoletoReason(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="text-amber-700" size={18} /> Anular baixa — {chargeRef(reverseBoletoOpen)}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">O boleto volta para "em aberto" e o faturamento vinculado volta para "aberto". O histórico da baixa anterior é preservado.</p>
          <div><Label>Motivo</Label><Textarea rows={4} value={reverseBoletoReason} onChange={e => setReverseBoletoReason(e.target.value)} data-testid="reverse-boleto-reason-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReverseBoletoOpen(null); setReverseBoletoReason(""); }}>Voltar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={doReverseBoleto} data-testid="confirm-reverse-boleto">Anular baixa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
